import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./app.css";
import Login from "./scenes/login/login";
import Topbar from "./scenes/global/Topbar";
import Sidenav from "./scenes/global/SideNav";
import Dashboard from "./scenes/dashboard/dashboard";
import ManageMember from "./scenes/member/manage_member";
import MemberList from "./scenes/member/member_list";
import ViewMember from "./scenes/member/view_member";
import MemberPayment from "./scenes/member/member_payment";
import FamilyList from "./scenes/family/family_list";
import ManageBranch from "./scenes/branch/manage_branch";
import BranchList from "./scenes/branch/branch_list";
import ManageSubscription from "./scenes/subscription/manage_subscription";
import SubscriptionList from "./scenes/subscription/subscription_list";
import ManageDiscipline from "./scenes/discipline/manage_discipline";
import DisciplineList from "./scenes/discipline/discipline_list";
// import AttendanceList from "./scenes/attendance/attendance_list"; // Hidden for performance
import AttendanceKiosk from "./scenes/attendance/AttendanceKiosk";
import AttendanceDashboard from "./scenes/attendance/AttendanceDashboard";
import GenerateReports from "./scenes/reports/generate_reports";
import GymRegistration from "./scenes/hpc/GymRegistration";
import HpcMemberList from "./scenes/hpc/HpcMemberList";
import HpcMemberProfile from "./scenes/hpc/HpcMemberProfile";
import HpcDashboard from "./scenes/hpc/HpcDashboard";
import HpcOverview from "./scenes/hpc/HpcOverview";
import HpuPaymentReceive from "./scenes/hpc/HpuPaymentReceive";
import PublicHpuReceipt from "./scenes/hpc/PublicHpuReceipt";
import TebaPaymentReceive from "./scenes/payments/TebaPaymentReceive";
import TebaOverview from "./scenes/teba/TebaOverview";
import PublicTebaReceipt from "./scenes/payments/PublicTebaReceipt";
import ManualSmsSender from "./scenes/hpc/ManualSmsSender";
import HpcPromotionList from "./scenes/hpc/promotions/HpcPromotionList";
import HpcPromotionForm from "./scenes/hpc/promotions/HpcPromotionForm";
import HpcPromotionDetails from "./scenes/hpc/promotions/HpcPromotionDetails";
import HpcPromotionReport from "./scenes/hpc/promotions/HpcPromotionReport";
import AssessmentList from "./scenes/hpc/assessment/AssessmentList";
import AssessmentMemberPicker from "./scenes/hpc/assessment/AssessmentMemberPicker";
import AssessmentWizard from "./scenes/hpc/assessment/AssessmentWizard";
import HpuAttendance from "./scenes/hpc/attendance/HpuAttendance";
import ZkDevice from "./scenes/hpc/attendance/ZkDevice";
import { CssBaseline, ThemeProvider, useMediaQuery, useTheme } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./components/AuthContext";
import { ModuleProvider } from "./context/ModuleContext";

// Layout wrapper component that conditionally shows sidebar/topbar
const MainLayout = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("md"));
  const noLayoutRoutes = ["/attendance-kiosk", "/login", "/hpc/register"];
  const isAssessmentWizard = /^\/hpc\/assessments\/\d+$/.test(location.pathname);
  const isPublicHpuReceipt = /^\/hpu\/receipt\/[^/]+$/.test(location.pathname)
    || /^\/teba\/receipt\/[^/]+$/.test(location.pathname);
  const isHpcMemberEdit = /^\/hpc\/members\/[^/]+\/edit$/.test(location.pathname);
  const isNoLayout = noLayoutRoutes.includes(location.pathname) || isAssessmentWizard || isPublicHpuReceipt || isHpcMemberEdit;

  if (isNoLayout) {
    // HPC wizard/public receipt pages render their own fullscreen layout (no sidebar/topbar, no login required)
    if (location.pathname === "/hpc/register" || isAssessmentWizard || isPublicHpuReceipt || isHpcMemberEdit) {
      return <>{children}</>;
    }
    return <div className="fullscreen-scroll">{children}</div>;
  }

  // Normal layout with sidebar and topbar
  return (
    <div className="app">
      <Sidenav className="sidenav" compactLayout={isCompactLayout} />
      <main className="main-content">
        <Topbar />
        {children}
      </main>
    </div>
  );
};

function App() {
  const [theme, colorMode] = useMode();

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ModuleProvider>
            <MainLayout>
              <Routes>
                {/* Attendance Kiosk - Standalone fullscreen route (protected) */}
                <Route
                  path="/attendance-kiosk"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <AttendanceKiosk />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/attendance-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <AttendanceDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/login"
                  element={<Login />}
                />
                <Route path="/hpc/register" element={<GymRegistration />} />
                {/* Public, unauthenticated receipt link (resolved solely by the opaque access token) */}
                <Route path="/hpu/receipt/:token" element={<PublicHpuReceipt />} />
                {/* Public, unauthenticated TEBA receipt link (signed token) */}
                <Route path="/teba/receipt/:token" element={<PublicTebaReceipt />} />
                <Route
                  path="/teba/payment-receive"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <TebaPaymentReceive />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teba/overview"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <TebaOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/overview"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/members"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcMemberList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/members/:membershipId/edit"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <GymRegistration />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/members/:membershipId"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcMemberProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-receive"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpuPaymentReceive />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/promotions"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcPromotionList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/promotions/new"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcPromotionForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/promotions/report"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcPromotionReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/promotions/:id"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcPromotionDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/promotions/:id/edit"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpcPromotionForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/manual-sms"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <ManualSmsSender />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/attendance"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <HpuAttendance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/attendance-device"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <ZkDevice />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/assessments"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <AssessmentList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/assessments/new"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <AssessmentMemberPicker />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hpc/assessments/:id"
                  element={
                    <ProtectedRoute allowedRoles={["hpc_admin"]}>
                      <AssessmentWizard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage_member"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <ManageMember />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/member_list"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin", "member"]}>
                      <MemberList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/view_member"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin", "member"]}>
                      <ViewMember />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/member_payment"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin", "member"]}>
                      <MemberPayment />
                    </ProtectedRoute>
                  }
                />
                {/* <Route
                  path="/update_family_owner"
                  element={
                    <ProtectedRoute>
                      <UpdateFamilyOwner />
                    </ProtectedRoute>
                  }
                /> */}
                <Route
                  path="/family_list"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <FamilyList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage_branch"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin"]}>
                      <ManageBranch />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/branch_list"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin"]}>
                      <BranchList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage_subscription"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin"]}>
                      <ManageSubscription />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription_list"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <SubscriptionList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage_discipline"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin"]}>
                      <ManageDiscipline />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/discipline_list"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <DisciplineList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/generate_reports"
                  element={
                    <ProtectedRoute allowedRoles={["global_admin", "branch_admin"]}>
                      <GenerateReports />
                    </ProtectedRoute>
                  }
                />
                {/* Attendance list hidden for performance reasons
                <Route
                  path="/attendance_list"
                  element={
                    <ProtectedRoute>
                      <AttendanceList />
                    </ProtectedRoute>
                  }
                />
                */}
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
            </MainLayout>
          </ModuleProvider>
        </AuthProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
